/**
 * Centralized Route Configuration
 *
 * This file contains all route definitions for the application.
 * All routes should be defined here and imported from this file.
 *
 * Route Structure:
 * - Public routes: accessible to everyone
 * - Protected routes: require authentication
 * - Client routes: require client role
 * - Admin routes: require admin role
 */

import { lazy } from 'react';

// Route types
export type RouteRole = 'public' | 'authenticated' | 'client' | 'admin';

export interface RouteConfig {
  path: string;
  element: React.LazyExoticComponent<React.ComponentType<any>>;
  roles?: RouteRole[];
  title?: string;
  description?: string;
  requiresAuth?: boolean;
}

// ============================================================================
// PUBLIC ROUTES
// ============================================================================

/**
 * Public routes that don't require authentication
 */
export const publicRoutes: RouteConfig[] = [
  {
    path: '/',
    element: lazy(() => import('@/app/pages/LandingPage')),
    roles: ['public'],
    title: 'Home',
    description: 'Landing page',
  },
  {
    path: '/pricing',
    element: lazy(() => import('@/app/pages/LandingPage')),
    roles: ['public'],
    title: 'Pricing',
    description: 'Pricing page',
  },
  {
    path: '/auth',
    element: lazy(() => import('@/app/components/route-wrappers/AuthPageWrapper')),
    roles: ['public'],
    title: 'Sign In',
    description: 'Authentication page',
  },
  {
    path: '/signup',
    element: lazy(() => import('@/features/auth/pages/SignUpPage')),
    roles: ['public'],
    title: 'Sign Up',
    description: 'Registration page',
  },
  {
    path: '/reset-password',
    element: lazy(() => import('@/features/auth/pages/PasswordResetPage')),
    roles: ['public'],
    title: 'Reset Password',
    description: 'Password reset page',
  },
  {
    path: '/admin-setup',
    element: lazy(() => import('@/features/admin/components/AdminSetup')),
    roles: ['public'],
    title: 'Admin Setup',
    description: 'Initial admin setup',
  },
  {
    path: '/checkout/:planId',
    element: lazy(() => import('@/app/components/route-wrappers/CheckoutPageWrapper')),
    roles: ['public'],
    title: 'Checkout',
    description: 'Subscription checkout',
  },
  {
    path: '/portal/:token',
    element: lazy(() => import('@/app/components/route-wrappers/ClientPortalPageWrapper')),
    roles: ['public'],
    title: 'Client Portal',
    description: 'Public client portal',
  },
  {
    path: '/q/:token',
    element: lazy(() => import('@/app/components/route-wrappers/PublicQuotePageWrapper')),
    roles: ['public'],
    title: 'Public Quote',
    description: 'Public quote viewing via share token',
  },
  {
    path: '/invoices/public/:token',
    element: lazy(() => import('@/app/components/route-wrappers/PublicInvoicePageWrapper')),
    roles: ['public'],
    title: 'Public Invoice',
    description: 'Public invoice viewing via token',
  },
  {
    path: '/privacy',
    element: lazy(() => import('@/app/pages/PrivacyPage')),
    roles: ['public'],
    title: 'Privacy Policy',
    description: 'Privacy policy page',
  },
  {
    path: '/terms',
    element: lazy(() => import('@/app/pages/TermsPage')),
    roles: ['public'],
    title: 'Terms of Service',
    description: 'Terms of service page',
  },
  {
    path: '/contact',
    element: lazy(() => import('@/app/pages/ContactPage')),
    roles: ['public'],
    title: 'Contact',
    description: 'Contact page',
  },
];

// ============================================================================
// CLIENT ROUTES
// ============================================================================

/**
 * Protected client dashboard routes
 * Base path: /client
 */

export const clientRoutes: RouteConfig[] = [
  {
    path: '/dashboard',
    element: lazy(() => import('@/features/dashboard/views/DashboardOverview')),
    roles: ['client'],
    title: 'Dashboard',
    description: 'Main dashboard overview',
  },
  {
    path: '/clients',
    element: lazy(() => import('@/features/clients/views/ClientsView')),
    roles: ['client'],
    title: 'Clients',
    description: 'Client management',
  },
  {
    path: '/services',
    element: lazy(() => import('@/features/services/views/ServicesView')),
    roles: ['client'],
    title: 'Services',
    description: 'Service catalog',
  },
  {
    path: '/quotes',
    element: lazy(() => import('@/features/quotes/views/QuotesView')),
    roles: ['client'],
    title: 'Quotes',
    description: 'Quote management',
  },
  {
    path: '/invoices',
    element: lazy(() => import('@/features/invoices/views/InvoicesView')),
    roles: ['client'],
    title: 'Invoices',
    description: 'Invoice management',
  },
  {
    path: '/calendar',
    element: lazy(() => import('@/features/calendar/views/CalendarView')),
    roles: ['client'],
    title: 'Calendar',
    description: 'Calendar and scheduling',
  },
  {
    path: '/expenses',
    element: lazy(() => import('@/features/expenses/views/ExpensesView')),
    roles: ['client'],
    title: 'Expenses',
    description: 'Expense tracking',
  },
  {
    path: '/subscription',
    element: lazy(() => import('@/features/subscription/views/SubscriptionView')),
    roles: ['client'],
    title: 'Subscription',
    description: 'Subscription management',
  },
  {
    path: '/templates',
    element: lazy(() => import('@/features/document-templates/views/TemplatesView')),
    roles: ['client'],
    title: 'Templates',
    description: 'Document templates',
  },
  // Settings routes
  {
    path: '/settings',
    element: lazy(() => import('@/features/settings/views/SettingsView')),
    roles: ['client'],
    title: 'Settings',
    description: 'Account settings',
  },
  {
    path: '/profile',
    element: lazy(() => import('@/features/user-profile/views/UserProfileView')),
    roles: ['client'],
    title: 'My Profile',
    description: 'User profile management',
  },
  {
    path: '/settings/general',
    element: lazy(() => import('@/features/settings/views/SettingsView')),
    roles: ['client'],
    title: 'General Settings',
    description: 'General account settings',
  },
  {
    path: '/settings/branding',
    element: lazy(() => import('@/features/settings/views/SettingsView')),
    roles: ['client'],
    title: 'Branding',
    description: 'Branding and appearance',
  },
  {
    path: '/settings/currency',
    element: lazy(() => import('@/features/settings/views/SettingsView')),
    roles: ['client'],
    title: 'Currency',
    description: 'Currency and exchange rates',
  },
  {
    path: '/settings/payments',
    element: lazy(() => import('@/features/settings/views/SettingsView')),
    roles: ['client'],
    title: 'Payment Methods',
    description: 'Payment method settings',
  },
];

// ============================================================================
// ADMIN ROUTES
// ============================================================================

/**
 * Protected admin dashboard routes
 * Base path: /admin
 */

export const adminRoutes: RouteConfig[] = [
  {
    path: '/dashboard',
    element: lazy(() => import('@/features/admin/views/AdminOverview')),
    roles: ['admin'],
    title: 'Admin Dashboard',
    description: 'Admin dashboard overview',
  },
  {
    path: '/users',
    element: lazy(() => import('@/features/admin/views/AdminUsersView')),
    roles: ['admin'],
    title: 'Users',
    description: 'User management',
  },
  {
    path: '/config',
    element: lazy(() => import('@/features/admin/views/AdminConfigView')),
    roles: ['admin'],
    title: 'Configuration',
    description: 'System configuration',
  },
  {
    path: '/email',
    element: lazy(() => import('@/features/admin/views/AdminEmailView')),
    roles: ['admin'],
    title: 'Email Templates',
    description: 'Email template management',
  },
  {
    path: '/finance',
    element: lazy(() => import('@/features/admin/views/AdminFinanceView')),
    roles: ['admin'],
    title: 'Finance',
    description: 'Financial overview',
  },
  {
    path: '/audit',
    element: lazy(() => import('@/features/admin/views/AdminAuditView')),
    roles: ['admin'],
    title: 'Audit Logs',
    description: 'System audit logs',
  },
  {
    path: '/templates',
    element: lazy(() => import('@/features/admin/views/AdminTemplatesView')),
    roles: ['admin'],
    title: 'Templates',
    description: 'Document templates',
  },
];

// ============================================================================
// ROUTE CONSTANTS
// ============================================================================

/**
 * Path constants for navigation
 * Use these instead of hardcoded strings
 */
export const ROUTES = {
  // Public
  HOME: '/',
  PRICING: '/pricing',
  AUTH: '/auth',
  SIGNUP: '/signup',
  RESET_PASSWORD: '/reset-password',
  ADMIN_SETUP: '/admin-setup',
  CHECKOUT: (planId: string) => `/checkout/${planId}`,
  PORTAL: (token: string) => `/portal/${token}`,
  PUBLIC_QUOTE: (token: string) => `/q/${token}`,
  PUBLIC_INVOICE: (token: string) => `/invoices/public/${token}`,
  PRIVACY: '/privacy',
  TERMS: '/terms',
  CONTACT: '/contact',

  // Client (base: /client)
  CLIENT_DASHBOARD: '/client/dashboard',
  CLIENT_HOME: '/client',
  CLIENTS: '/client/clients',
  SERVICES: '/client/services',
  QUOTES: '/client/quotes',
  INVOICES: '/client/invoices',
  CALENDAR: '/client/calendar',
  EXPENSES: '/client/expenses',
  SUBSCRIPTION: '/client/subscription',
  TEMPLATES: '/client/templates',

  // Settings
  SETTINGS: '/client/settings',
  PROFILE: '/client/profile',
  SETTINGS_GENERAL: '/client/settings/general',
  SETTINGS_BRANDING: '/client/settings/branding',
  SETTINGS_CURRENCY: '/client/settings/currency',
  SETTINGS_PAYMENTS: '/client/settings/payments',

  // Admin (base: /admin)
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_USERS: '/admin/users',
  ADMIN_CONFIG: '/admin/config',
  ADMIN_EMAIL: '/admin/email',
  ADMIN_FINANCE: '/admin/finance',
  ADMIN_AUDIT: '/admin/audit',
  ADMIN_TEMPLATES: '/admin/templates',
} as const;

// ============================================================================
// ROUTE HELPERS
// ============================================================================

/**
 * Check if a path is a public route
 */
export const isPublicRoute = (path: string): boolean => {
  return publicRoutes.some(route => route.path === path);
};

/**
 * Check if a path is a client route
 */
export const isClientRoute = (path: string): boolean => {
  return path.startsWith('/client');
};

/**
 * Check if a path is an admin route
 */
export const isAdminRoute = (path: string): boolean => {
  return path.startsWith('/admin');
};

/**
 * Get route title by path
 */
export const getRouteTitle = (path: string): string => {
  const allRoutes = [...publicRoutes, ...clientRoutes, ...adminRoutes];
  const route = allRoutes.find(r => path.match(new RegExp(`^${r.path.replace(/:[^/]+/g, '[^/]+')}$`)));
  return route?.title || 'Page';
};
